import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ECharts } from 'echarts/core';
import { ComposeOption, init as echartsInit, use as echartsUse } from 'echarts/core';
import { PieChart as EChartsPie, PieSeriesOption } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  TransformComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import {
  DatasetComponentOption,
  GridComponentOption,
  LegendComponentOption,
  TooltipComponentOption,
} from 'echarts';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { styled } from '@mui/material/styles';
import { useTheme, Typography } from '@mui/material';
import { PieCallbackDataParams } from 'echarts/types/src/chart/pie/PieSeries';
import { formatUSDShort } from '../../../util/formatters/NumericFormatters';
import { ChartTitleWrapper } from '../ChartTitleWrapper';
import { ChartWrapper } from '../ChartWrapper/ChartWrapper';
import { IconTypes } from '../../Icon';
import { generateChartColourScheme } from '../generateChartColourScheme';
import { Chart, ChartSizes } from '../chart-utils';
import { ECHARTS_V5_THEME } from '../echarts-v5-theme';
import { ItemsColourScheme } from '../../../util/ColourSchemeGenerator';
import { useIsInViewport } from '../../../hooks/useIsInViewport';
import { IPortfolioChartProps } from '../../../pages/PortfolioOverview/components/ChartsSection/ChartComponent';
import { FMT } from '../../../util/formatter-service';

type ECPieOption = ComposeOption<
  | DatasetComponentOption
  | GridComponentOption
  | LegendComponentOption
  | PieSeriesOption
  | TooltipComponentOption
>;

// Register the required components
echartsUse([
  TooltipComponent,
  LegendComponent,
  DatasetComponent,
  GridComponent,
  TransformComponent,
  EChartsPie,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
]);

const margin = { top: 60, right: 60, bottom: 60, left: 60 };

export function formatTotal(value: number) {
  return FMT.format('currencyShort', value);
}

const PieWrapper = styled('div')`
  text-align: center;
  position: relative;
  width: 100%;
  height: 100%;
`;

const TotalWrapper = styled('div')`
  position: absolute;
  top: 0;
  right: ${margin.right}px;
  bottom: 0;
  left: ${margin.left}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
`;

const tooltipTextStyle = {
  fontFamily: 'Wotfard-Regular',
  fontSize: 12,
};

type DataItem = {
  name: string;
  value: number;
  id: string;
};

export interface PieChartProps extends Pick<
  IPortfolioChartProps<DataItem>,
  'isFullScreen' | 'onOpenFullScreen'
> {
  caption?: string;
  chartColors?: ItemsColourScheme<string>;
  data: DataItem[];
  id: string;
  title: string;
  total: number;
  width?: number;
}

export const PieChart = memo(function PieChart({
  caption,
  chartColors,
  data,
  id,
  title,
  total,
  width = 700,
  isFullScreen,
  onOpenFullScreen,
}: PieChartProps) {
  const [showMore, setShowMore] = useState(false);
  const [widthState, setWidthState] = useState<string | number>(width);
  const [size, setSize] = useState('1/4 Screen');
  const [isHovering, setIsHovering] = useState(false);
  const { neutral } = useTheme().colors;
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const pieChartRef = useRef<ECharts | null>(null);
  const totalLabel = formatTotal(total);
  const isInViewport = useIsInViewport(
    {
      threshold: 0.6,
    },
    chartContainerRef
  );
  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setShowMore(false);
    setIsHovering(false);
  };

  useLayoutEffect(() => {
    const updateSize = () => {
      setWidthState(ChartSizes[size as keyof typeof ChartSizes]);
    };

    updateSize();
  }, [size]);

  useEffect(() => {
    const container = chartContainerRef.current;

    if (!container) {
      return;
    }

    if (!pieChartRef.current) {
      pieChartRef.current = echartsInit(container, ECHARTS_V5_THEME);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          pieChartRef.current!.resize();
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, [chartContainerRef]);

  useEffect(() => {
    if (!(pieChartRef.current && isInViewport)) {
      return;
    }
    const radius = isFullScreen ? ['40%', '90%'] : ['25%', '55%'];

    const pieChartData: PieSeriesOption = {
      type: 'pie',
      radius,
      itemStyle: {
        borderRadius: 5,
        borderColor: '#fff',
        borderWidth: 2,
        color: chartColors
          ? (params: PieCallbackDataParams) => {
              return chartColors?.get(params.name) ?? 'grey';
            }
          : undefined,
      },
      label: {
        fontFamily: 'Wotfard-Regular',
        fontSize: isFullScreen ? 12 : 10,
        position: 'outer',
        bleedMargin: 0,
      },
      labelLine: {
        length: 3,
      },
      data,
    };

    const option: ECPieOption = {
      color: chartColors ? null : generateChartColourScheme(data.length),
      grid: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      series: [pieChartData],
      tooltip: {
        ...tooltipTextStyle,
        formatter: (params) => {
          const { name, value } = (params as CallbackDataParams).data as DataItem;
          const pctValue = `${((value / total) * 100).toFixed(2)}%`;
          return `${name} ${formatUSDShort(value)} (${pctValue})`;
        },
        trigger: 'item',
        confine: true,
      },
    };

    if (pieChartRef.current) {
      pieChartRef.current.setOption(option, true);
    }
  }, [chartColors, data, isFullScreen, isInViewport, total]);

  return (
    <ChartWrapper
      id={id}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      width={widthState}
      dataTestid={'pie-chart'}
    >
      <ChartTitleWrapper
        isHovering={isHovering}
        title={title}
        showMore={showMore}
        setShowMore={setShowMore}
        id={id}
        caption={caption}
        icon={IconTypes.FINANCE}
        handleSizeChange={setSize}
        size={size}
        refProp={chartContainerRef}
        isFullScreen={isFullScreen}
        handleFullScreen={onOpenFullScreen}
      />
      <PieWrapper>
        <Chart width={'100%'} height={isFullScreen ? '100%' : undefined} ref={chartContainerRef} />
        <TotalWrapper>
          <Typography variant={'body2'} color={neutral[70]}>
            {totalLabel}
          </Typography>
        </TotalWrapper>
      </PieWrapper>
    </ChartWrapper>
  );
});
